import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { styled } from '../../../common.styles';
import DOMPurify from 'dompurify';
import { EmbedContext } from '../../../../embedding/embed-root';

const RichtextContainer = styled.div<{ disableOverflow: boolean; overFlowWrap?: string; minMaxHeight?: number }>`
  width: 100%;
  height: fit-content;
  word-break: break-word;
  max-height: ${(p) => (p.minMaxHeight ? p.minMaxHeight + 'px' : 'fit-content')};
  min-height: ${(p) => (p.minMaxHeight ? p.minMaxHeight + 'px' : 'auto')};
  overflow: ${(p) => (p.disableOverflow ? 'unset' : 'auto')};
  overflow-wrap: ${(p) => p.overFlowWrap && p.overFlowWrap};

  /* for wicket added baseline vertical-align and don't work sup and sub tags */
  p {
    sub {
      vertical-align: sub !important;
    }

    sup {
      vertical-align: super !important;
    }
  }
`;

export interface Props {
  content?: string | null;
  className?: string;
  disableOverflow?: boolean;
  overFlowWrap?: string;
  onClick?: (e: React.MouseEvent) => void;
  generatedScriptId?: string;
  minMaxHeight?: number;
}

const DOMPurifyConfig = {
  ADD_ATTR: ['target', 'rel', 'onclick'],
  ADD_TAGS: ['iframe'],
  FORCE_BODY: true,
};

/**
 * A lot of html template coming from the backend has unnecessary html comments inside style tags, so just remove them.
 */
const removeHtmlCommentsFromStyleTags = (content: string | null | undefined) =>
  (content ?? '').split('<style><!--').join('<style>').split('--></style>').join('</style>');

let _richTextInstanceCounter = 0;

const traverseAndTransformContent = (node: HTMLElement, lang: string) => {
  const elementStyle = window.getComputedStyle(node);
  const cssTextTransform = elementStyle.textTransform;

  if (cssTextTransform?.toLowerCase() === 'uppercase') {
    node.innerText = node.innerText.toLocaleUpperCase(lang);
  }

  if (node.children && node.children.length) {
    Array.from(node.children).forEach((child) => {
      traverseAndTransformContent(child as HTMLElement, lang);
    });
  }
};

export const RichText = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const disableOverflow = props.disableOverflow || false;
  const htmlTabContent = useMemo(() => {
    let preparedContent = removeHtmlCommentsFromStyleTags(props.content);
    preparedContent = preparedContent.replace('data-tooltip-id="global-brand-tooltip"', '');
    return {
      __html: DOMPurify?.sanitize(preparedContent, DOMPurifyConfig) ?? '',
    };
  }, [props.content, DOMPurify]);

  const [instanceId] = useState(_richTextInstanceCounter++);
  const [refreshCount, setRefreshCount] = useState(0);
  const embedStore = useContext(EmbedContext);
  const textRef = useRef(null);

  useEffect(() => {
    const newRefreshCount = refreshCount + 1;
    const scriptElement = document.createElement('script');
    scriptElement.setAttribute('rich-text-content-' + instanceId + '-' + newRefreshCount, '');
    const additionalScriptElements: HTMLScriptElement[] = [];

    const scriptsRegex = /<script[^>]*>([\s\S]*?)<\/script>/gm;
    const scriptSrcRegex = /src=["']([^"']*)["']/gm;
    const cDataReplaceRegex = /\/\/ *<!\[CDATA\[(.*?)\/\/ *]]>/gm;
    const jquerySelectorsRegex = /\$\((?:("[^"]*")|('[^']*'))\)/gm;
    const styleTagRegex = /<style([^>]*)>/gm;

    setRefreshCount(newRefreshCount);

    let scriptContent = scriptsRegex.exec(props.content as string);
    while (scriptContent != null) {
      const src = scriptSrcRegex.exec(scriptContent?.[0] || '')?.[1];

      if (src !== undefined) {
        const scriptToLoad = document.createElement('script');
        scriptToLoad.setAttribute('rich-text-content-' + instanceId + '-' + newRefreshCount, '');
        scriptToLoad.src = src;
        additionalScriptElements.push(scriptToLoad);
      }

      let content = scriptContent[1].replace(cDataReplaceRegex, '$1');
      let selectorMatch = jquerySelectorsRegex.exec(content);
      while (selectorMatch != null) {
        const selector = selectorMatch[1] || selectorMatch[2];
        const strippedSelector = selector.replace(/['"]*/gm, '');
        if (strippedSelector.indexOf('body') !== 0 && strippedSelector.indexOf('head') !== 0) {
          const encapsuleSelector = '.RichTextInstance' + instanceId + '-' + newRefreshCount + ' ';
          const replacedText = (selector?.slice(0, 1) ?? '') + encapsuleSelector + (selector?.slice(1) ?? '');
          content = content.replace(selector, replacedText);
        }
        selectorMatch = jquerySelectorsRegex.exec(content);
      }

      let styleMatch = styleTagRegex.exec(content);
      while (styleMatch != null) {
        content = content.replace(
          styleMatch[0],
          '<style rich-text-content-' + instanceId + '-' + newRefreshCount + ' ' + styleMatch[1] + '>'
        );
        styleMatch = styleTagRegex.exec(content);
      }

      scriptElement.innerText += content;
      scriptContent = scriptsRegex.exec(props.content as string);
    }

    if (scriptElement.innerText) {
      setTimeout(() => {
        document.body.appendChild(scriptElement);
      }, 300);
    }

    if (additionalScriptElements.length > 0) {
      setTimeout(() => {
        additionalScriptElements.forEach((additionalScriptElement) => {
          document.body.appendChild(additionalScriptElement);
        });
      }, 300);
    }

    setTimeout(() => {
      const htmlLang = document.getElementsByTagName('html')[0].getAttribute('lang') || 'en';
      // DWT-50020 - uppercased greek characters has some problems if uppercased with CSS - we uppercase it with toLocaleUpperCase()
      if (htmlLang === 'el') {
        const container = document.querySelector(
          '.RichTextInstance' + instanceId + '-' + newRefreshCount
        ) as HTMLElement;
        if (container) {
          traverseAndTransformContent(container, htmlLang);
        }
      }
    }, 300);

    return () => {
      setTimeout(() => {
        const elements = document.querySelectorAll('*[rich-text-content-' + instanceId + '-' + newRefreshCount + ']');
        elements.forEach((element) => {
          element.remove();
        });
      }, 300);
    };
  }, [props.content]);

  // Clean up empty <p> tags
  const cleanEmptyPTags = (text: string) => {
    const { countryCode, brand, dealerCode } = embedStore?.decodedJwtToken || {};
    if (countryCode === 'HU' && brand === 'FIAT' && dealerCode.includes('FIHU')) {
      // For Hungary FIAT dealers, return simple text without cleaning empty <p> tags
      return text;
    }

    return text.replace(/<p[^>]*>(\s|&nbsp;|&#160;)*<\/p>/gi, '');
  };

  // Handle new tooltips logic, new tooltip use react-tooltip package
  const newModifiedHtmlWithTooltip = useMemo(() => {
    const newModifiedHtml = htmlTabContent.__html.replace(/class="richtext-tooltip"/g, () => {
      return `class="richtext-tooltip" data-tooltip-id=${embedStore.tooltipContainerId}`;
    });
    return cleanEmptyPTags(newModifiedHtml);
  }, [htmlTabContent.__html]);

  useEffect(() => {
    const handleKeyDown = (event: any) => {
      if (event.ctrlKey && event.key === 'a' && textRef?.current) {
        event.preventDefault();
        const range = document.createRange();
        range.selectNodeContents(textRef?.current);

        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    };

    const container: any = textRef?.current;
    if (container) {
      container.addEventListener('click', () => {
        document.addEventListener('keydown', handleKeyDown);
      });
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  if (!props.content || !DOMPurify || !htmlTabContent) {
    return null;
  }

  return (
    <>
      <RichtextContainer
        minMaxHeight={props.minMaxHeight}
        disableOverflow={disableOverflow}
        overFlowWrap={props.overFlowWrap}
        ref={ref || textRef}
        className={
          props.className !== undefined
            ? props.className + ' RichTextInstance' + instanceId + '-' + refreshCount
            : 'RichTextInstance' + instanceId + '-' + refreshCount
        }
        onClick={props.onClick}
        dangerouslySetInnerHTML={{
          __html: newModifiedHtmlWithTooltip.padStart(refreshCount - 1, ' '),
        }}
      />
    </>
  );
});
